import { useState, useEffect } from 'react';
import { Breadcrumbs, ContentLayout } from 'components/Layout';
import { FiSearch } from 'react-icons/fi';
import { Button, ForbiddenFallback, Spinner } from 'components/Elements';

import { ActiveCustomers } from '../component/ActiveCustomers';
import { InactiveCustomers } from '../component/InactiveCustomers';
import { useActiveCustomers } from '../api/getActiveCustomers';
import { useInActiveCustomers } from '../api/getInactiveCustomers';
import { searchCustomers } from '../api/searchCustomers';
import { useDebounce } from 'hooks/useDebounce';
import { SearchResultsTable } from '../component/SearchResults';
import { FiltersDrawer } from '../component/Filters';
import { Authorization, ROLES } from 'lib/authorization';
import { ChevronDown, Sliders } from 'lucide-react';
import { ActiveUsers } from '../component/ActiveUsers';
import { SendMessage } from '../component/SendMessage';

export const Users = () => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedSearchText = useDebounce(searchText, 500);

  const activeQuery = useActiveCustomers({ pageNumber: 1 });
  const inactiveQuery = useInActiveCustomers({ pageNumber: 1 });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const activeCount = activeQuery?.data?.activeUsersTotal || 0;
  const inactiveCount = inactiveQuery?.data?.inActiveUsersTotal || 0;

  // ✅ Define TABS before useState
  const TABS = [
    { label: 'Active Users', count: activeCount },
    { label: 'Inactive Users', count: inactiveCount },
  ];

  const [activeTab, setActiveTab] = useState(TABS[0].label);

  useEffect(() => {
    if (!debouncedSearchText) {
      setSearchResults(null);
      return;
    }

    const fetchSearch = async () => {
      setIsSearching(true);
      try {
        const response = await searchCustomers({
          text: debouncedSearchText,
          customerId: 1,
        });
        setSearchResults(response?.data?.customers || []);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    fetchSearch();
  }, [debouncedSearchText]);

  const renderContent = () => {
    if (searchResults) return <SearchResultsTable results={searchResults} />;
    switch (activeTab) {
      case 'Active Users':
        return <ActiveUsers />;
      case 'Inactive Users':
        return <InactiveCustomers />;
      default:
        return null;
    }
  };

  const openFiltersDrawer = () => {
    setIsDrawerOpen(true);
  };

  return (
    <Authorization
      allowedRoles={[
        ROLES.ROOT_USER,
        ROLES.TRANSACTION_MANAGER,
        ROLES.TECHNICAL,
        ROLES.CUSTOMER_SUPPORT,
      ]}
      // requiredPermissions={['manageUsers', 'manageRoles']}
      forbiddenFallback={<ForbiddenFallback />}
    >
      <ContentLayout
        title="Users"
        breadcrumbs={<Breadcrumbs />}
        end={
          <div className="flex flex-row items-center space-x-2">
            <div className="relative flex items-center">
              <span className="flex items-center justify-center w-7 h-7 rounded-full  text-white">
                {isSearching ? <Spinner size="sm" /> : <FiSearch />}
              </span>
              <input
                type="text"
                placeholder="Search for user..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-4 pr-4 w-[16vw] text-sm text-white placeholder-white/60 rounded-3xl bg-transparent  focus:outline-none focus:ring-0 focus:ring-white/20"
              />
            </div>
            <Authorization
              allowedRoles={[ROLES.ROOT_USER, ROLES.CUSTOMER_SUPPORT]}
              // requiredPermissions={['manageUsers', 'manageRoles']}
              forbiddenFallback={<div></div>}
            >
              <div className="">
                <SendMessage />
              </div>
            </Authorization>
          </div>
        }
      >
        <div>
          {!searchResults && (
            <div className="bg-white/10 flex flex-row items-center justify-between rounded-xl my-4">
              <div className="flex space-x-4">
                {TABS.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => {
                      setActiveTab(tab.label);
                      setSearchResults(null);
                    }}
                    className={`py-2 px-4 transition-all duration-200 ${
                      activeTab === tab.label
                        ? 'bg-gray-100/20 rounded-xl m-1 text-[#fafeff]'
                        : 'text-white/30 hover:text-white/50'
                    }`}
                  >
                    {tab.label}
                    <span className="ml-2">({tab.count})</span>
                  </button>
                ))}
              </div>
              {/* <button
                className="flex text-white pr-6 flex-row items-center space-x-2"
                onClick={openFiltersDrawer}
              >
                <Sliders className="text-white" size={16} />
                <span className="">Filters</span>
              </button> */}
            </div>
          )}

          {/* <FiltersDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
          /> */}
          <div>{renderContent()}</div>
        </div>
      </ContentLayout>
    </Authorization>
  );
};
